<template>
  <div class="min-h-screen bg-primary w-full h-full mt-[60px] lg:mt-0">
    <div v-if="!isLoading">
      <page-wrapper v-if="pageGUI" :page-gui="pageGUI">
        <div class="min-h-screen bg-primary w-full h-full">
          <section :class="containerStyles">
            <div
              class="col-span-12 col-start-2 row-start-2 md:col-start-2 md:col-span-5 md:row-start-1"
            >
              <image-component
                v-if="image"
                :image="image"
                :img-alt="pageProperties?.imageAltText"
                classes="w-full object-cover"
              />
            </div>
            <div
              class="col-span-12 col-start-2 md:col-span-4 md:col-start-9 row-start-1 flex flex-col justify-center items-start"
            >
              <div v-if="pageProperties?.title" :class="subheaderStyles">
                {{ pageProperties?.title }}
              </div>
              <rich-text
                v-if="getMarkup"
                :text="getMarkup"
                :isUmbraco="false"
                :theme="themeType"
                :isManchet="true"
              />
              <link-button
                v-if="pageProperties?.headerLink"
                :label="linkLabel"
                :url="linkUrl"
                :target="linkTarget"
                classes="mb-8 mt-8 md:mt-[60px] self-start"
                :theme="themeType"
              />
            </div>
          </section>
        </div>
      </page-wrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, onMounted, computed, toValue } from "vue";
import { parseUrl, changeTheme } from "@/util/helper";
import { Theme } from "@/types/types";
import { getPage, getTranslations, getPageByID } from "@/util/api";
import { IErrorPage } from "@/types/IErrorPage";
import ImageComponent from "@atoms/Image/Img.vue";
import { getThemeType } from "@/util/helper";
import RichText from "@components/Typography/RichText.ce.vue";
import LinkButton from "@atoms/LinkButton/LinkButton.ce.vue";
import PageWrapper from "@components/PageWrapper.vue";

const isLoading = ref(true);
const environment = ref();
const urlParams = ref<String[] | null>(null);

const pageData = ref<IErrorPage | null>(null);
const pageGUI = ref<string | undefined>();

const theme = ref<Theme | null>(null);

const iconArray = ref<Array<{ icon: string }>>([]);
const currentUrl = ref<string | null>(null);
const shareLabel = ref<string>("");

const isPreview = ref<boolean>(false);
const previewUrl = ref<string>("");
const pageKey = ref<string | undefined>();

onBeforeMount(async () => {
  pageKey.value = document.documentElement.dataset.id;
  const metaUrl = document.head.querySelector("meta[property='og:url']");
  if (metaUrl instanceof HTMLMetaElement) {
    previewUrl.value = metaUrl.content.replace(/^https?:\/\/[^/]+/, "");
  }

  environment.value = parseUrl(window.location.href).environment;
  urlParams.value = parseUrl(window.location.href).params;

  //create social share icons
  currentUrl.value = window.location.href;
  iconArray.value = [
    { icon: "Facebook" },
    { icon: "Twitter" },
    { icon: "LinkedIn" },
    { icon: "MailTo" },
  ];
  shareLabel.value = await getTranslations("ShareTitle");

  if (!urlParams.value) return;

  let path = urlParams.value.join("/");
  if (previewUrl.value.indexOf(path) < 0) {
    path = previewUrl.value;
    isPreview.value = true;
  }
  if (path) {
    pageData.value = (await getPageByID(pageKey.value, isPreview.value)) as IErrorPage;
    //pageData.value = (await getPage(path, isPreview.value)) as IErrorPage;
    if (pageData.value) {
      theme.value = pageData.value.properties.backgroundColor.label;
      if (theme.value) changeTheme(theme.value);
      pageGUI.value = pageData.value.route.startItem.id;
      isLoading.value = false;
    }
  }
});

const pageProperties = computed(() => {
  return pageData.value?.properties;
});

const image = computed(() => {
  return pageProperties.value?.image
    ? JSON.stringify(pageProperties.value.image[0])
    : null;
});

const containerStyles = computed(() => {
  const bgColor = `bg-${theme.value}`;
  return `${bgColor} sectionContainer min-h-1/2`;
});

const themeType = computed(() => {
  const color = pageProperties.value?.backgroundColor?.label
    ? pageProperties.value?.backgroundColor?.label
    : "sand";
  return getThemeType(color);
});

const getMarkup = computed(() => {
  return pageProperties.value?.description?.markup;
});

const isUmbraco = computed(() => {
  return false;
});

const linkUrl = computed(() => {
  if (pageProperties.value?.headerLink) {
    if (pageProperties.value?.headerLink[0]?.url) {
      return pageProperties.value?.headerLink[0]?.url;
    }
    console.log("has link", pageProperties.value?.headerLink[0]?.route.path);
    return pageProperties.value?.headerLink[0]?.route.path;
  }
});

const linkLabel = computed(() => {
  if (pageProperties.value?.headerLink) {
    return pageProperties.value?.headerLink[0]?.title;
  }
});

const linkTarget = computed(() => {
  if (pageProperties.value?.headerLink && pageProperties.value?.headerLink[0]?.target) {
    return pageProperties.value?.headerLink[0]?.target;
  }
  return "_self";
});

const subheaderStyles = computed(() => {
  const color =
    isUmbraco && themeType.value === "dark"
      ? "text-white"
      : isUmbraco && themeType.value === "light"
      ? "text-aubergine"
      : "text-typography";
  return `${color} text-xs md:text-sm font-semibold mb-10 md:mb-20`;
});
</script>
